import { useState, useMemo } from "react"
import { FolderOpen, Plus, Edit2, Trash2, Trophy, BookOpen, Users, Zap, X, ChevronDown, ChevronUp, ExternalLink } from "lucide-react"
import { Sidebar } from "../components/ui/sidebar"
import ProtectedRoute from "../components/ProtectedRoute"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"

const CATEGORIES = [
  { id: "project", label: "Projects", icon: Zap, color: "bg-indigo-50 border-indigo-200 text-indigo-700" },
  { id: "competition", label: "Competitions", icon: Trophy, color: "bg-amber-50 border-amber-200 text-amber-700" },
  { id: "extracurricular", label: "Extracurriculars", icon: Users, color: "bg-teal-50 border-teal-200 text-teal-700" },
  { id: "course", label: "Courses & Certs", icon: BookOpen, color: "bg-violet-50 border-violet-200 text-violet-700" },
]

function EntryForm({ initial, onSave, onCancel }) {
  const [title, setTitle] = useState(initial?.title ?? "")
  const [category, setCategory] = useState(initial?.category ?? "project")
  const [description, setDescription] = useState(initial?.description ?? "")
  const [date, setDate] = useState(initial?.date ?? "")
  const [outcome, setOutcome] = useState(initial?.outcome ?? "")
  const [link, setLink] = useState(initial?.link ?? "")

  const valid = title.trim().length > 0

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4">
      <h3 className="font-black text-slate-900">{initial ? "Edit Entry" : "Add Portfolio Entry"}</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Title *</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. School Science Fair Project"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Category</label>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
          >
            {CATEGORIES.map(c => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={3}
          placeholder="What did you do? What did you learn?"
          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Date / Period</label>
          <input
            value={date}
            onChange={e => setDate(e.target.value)}
            placeholder="e.g. March 2025 or 2024–2025"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Outcome / Award</label>
          <input
            value={outcome}
            onChange={e => setOutcome(e.target.value)}
            placeholder="e.g. 2nd place, Grade A, Completed"
            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-1 block">Link (optional)</label>
        <input
          value={link}
          onChange={e => setLink(e.target.value)}
          placeholder="https://github.com/..."
          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <button
          onClick={onCancel}
          className="flex-1 border border-slate-200 text-slate-600 font-semibold py-2.5 rounded-xl hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave({ title: title.trim(), category, description, date, outcome, link })}
          disabled={!valid}
          className="flex-1 bg-indigo-600 text-white font-bold py-2.5 rounded-xl hover:bg-indigo-700 disabled:opacity-40"
        >
          {initial ? "Save Changes" : "Add Entry"}
        </button>
      </div>
    </div>
  )
}

function EntryCard({ entry, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false)
  const cat = CATEGORIES.find(c => c.id === entry.category) ?? CATEGORIES[0]
  const Icon = cat.icon

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      <div className="p-4 flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${cat.color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="font-bold text-slate-800 text-sm">{entry.title}</p>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => onEdit(entry)} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
                <Edit2 className="w-3.5 h-3.5 text-slate-400" />
              </button>
              <button onClick={() => onDelete(entry.id)} className="p-1 hover:bg-red-50 rounded-lg transition-colors">
                <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            {entry.date && <span className="text-xs text-slate-400">{entry.date}</span>}
            {entry.outcome && (
              <span className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                {entry.outcome}
              </span>
            )}
            {entry.link && (
              <a
                href={entry.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-indigo-500 flex items-center gap-0.5 hover:underline"
              >
                <ExternalLink className="w-3 h-3" />
                View
              </a>
            )}
          </div>
          {entry.description && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="mt-1.5 flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? "Hide" : "Show"} details
            </button>
          )}
          {expanded && entry.description && (
            <p className="mt-2 text-xs text-slate-500 leading-relaxed">{entry.description}</p>
          )}
        </div>
      </div>
    </div>
  )
}

function loadPortfolio(userId) {
  const all = StorageService.read("portfolio") ?? []
  return all.filter(e => e.userId === userId)
}

function savePortfolio(userId, entries) {
  const all = (StorageService.read("portfolio") ?? []).filter(e => e.userId !== userId)
  StorageService.write("portfolio", [...all, ...entries])
}

export default function PortfolioPage() {
  const { userId, autoEntries } = useMemo(() => {
    const currentUser = AuthStore.getState().currentUser
    const students = StorageService.read("students") ?? []
    const student = students.find(s => s.userId === currentUser?._id || s.email === currentUser?.email) ?? students[0]
    const uid = student?.userId ?? student?._id ?? "anon"

    // Pull self-reported activity log entries as auto-suggestions
    const activityLog = student?.activityLog ?? []
    const auto = activityLog
      .filter(e => ["extracurricular", "club", "competition", "volunteering"].includes(e.eventType) && e.eventCategory === "self_reported")
      .map(e => ({
        id: `auto-${e.id ?? Math.random()}`,
        category: e.eventType === "competition" ? "competition" : "extracurricular",
        title: e.meta?.activityName ?? e.meta?.clubName ?? e.meta?.name ?? e.eventType,
        date: e.timestamp?.slice(0, 10) ?? "",
        description: e.meta?.note ?? "",
        outcome: e.meta?.placement ?? "",
        link: "",
        auto: true,
      }))
      .filter(e => e.title)

    return { userId: uid, autoEntries: auto }
  }, [])

  const [entries, setEntries] = useState(() => loadPortfolio(userId))
  const [showForm, setShowForm] = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [activeFilter, setActiveFilter] = useState("all")

  const persist = (updated) => {
    setEntries(updated)
    savePortfolio(userId, updated)
  }

  const handleAdd = (data) => {
    const entry = { ...data, id: Date.now().toString(), userId, createdAt: new Date().toISOString() }
    persist([...entries, entry])
    setShowForm(false)
  }

  const handleEdit = (data) => {
    persist(entries.map(e => e.id === editTarget.id ? { ...e, ...data } : e))
    setEditTarget(null)
  }

  const handleDelete = (id) => {
    persist(entries.filter(e => e.id !== id))
  }

  const importAuto = (auto) => {
    const exists = entries.some(e => e.title === auto.title)
    if (exists) return
    const entry = { ...auto, id: Date.now().toString(), userId, createdAt: new Date().toISOString(), auto: false }
    persist([...entries, entry])
  }

  const filtered = activeFilter === "all" ? entries : entries.filter(e => e.category === activeFilter)
  const counts = Object.fromEntries(CATEGORIES.map(c => [c.id, entries.filter(e => e.category === c.id).length]))

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        <main className="flex-1 lg:ml-64 p-4 md:p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">

            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-black text-slate-900">My Portfolio</h1>
                <p className="text-slate-500 text-sm mt-1">Document your projects, competitions, and activities.</p>
              </div>
              <button
                onClick={() => { setShowForm(true); setEditTarget(null) }}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-5 py-2.5 rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Entry
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveFilter(activeFilter === cat.id ? "all" : cat.id)}
                    className={`bg-white rounded-2xl border p-4 text-left transition-colors ${
                      activeFilter === cat.id ? "border-indigo-400 bg-indigo-50" : "border-slate-200 hover:border-indigo-200"
                    }`}
                  >
                    <Icon className="w-5 h-5 text-slate-500 mb-2" />
                    <p className="text-2xl font-black text-slate-900">{counts[cat.id] ?? 0}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{cat.label}</p>
                  </button>
                )
              })}
            </div>

            {/* Add/edit form */}
            {showForm && !editTarget && (
              <EntryForm onSave={handleAdd} onCancel={() => setShowForm(false)} />
            )}
            {editTarget && (
              <EntryForm initial={editTarget} onSave={handleEdit} onCancel={() => setEditTarget(null)} />
            )}

            {/* Auto-import from activity log */}
            {autoEntries.length > 0 && entries.length === 0 && (
              <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5 space-y-3">
                <p className="text-sm font-bold text-teal-800">We found activities from your log — add them to your portfolio?</p>
                <div className="space-y-2">
                  {autoEntries.slice(0, 4).map((auto, i) => (
                    <div key={i} className="flex items-center justify-between bg-white border border-teal-100 rounded-xl px-4 py-2.5">
                      <div>
                        <p className="text-sm font-semibold text-slate-700">{auto.title}</p>
                        {auto.date && <p className="text-xs text-slate-400">{auto.date}</p>}
                      </div>
                      <button
                        onClick={() => importAuto(auto)}
                        className="text-xs font-bold text-teal-700 border border-teal-300 px-3 py-1 rounded-lg hover:bg-teal-100 transition-colors"
                      >
                        Import
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Entry list */}
            {filtered.length > 0 ? (
              <div className="space-y-3">
                {filtered.map(entry => (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    onEdit={e => { setEditTarget(e); setShowForm(false) }}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            ) : (
              !showForm && (
                <div className="text-center py-16 space-y-3">
                  <FolderOpen className="w-12 h-12 text-slate-300 mx-auto" />
                  <p className="font-bold text-slate-700">
                    {activeFilter === "all" ? "Your portfolio is empty" : `No ${CATEGORIES.find(c => c.id === activeFilter)?.label ?? "entries"} yet`}
                  </p>
                  <p className="text-slate-400 text-sm">Add projects, competitions, and activities to showcase your journey.</p>
                  <button
                    onClick={() => setShowForm(true)}
                    className="inline-flex items-center gap-2 bg-indigo-600 text-white font-bold px-5 py-2.5 rounded-xl hover:bg-indigo-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add your first entry
                  </button>
                </div>
              )
            )}

          </div>
        </main>
      </div>
    </ProtectedRoute>
  )
}
